import { Loader2 } from "lucide-react";
import t from "@/lang/lang";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useGfsOfferConfigGetQuery } from "@/services/backend/gfs/offer/api";
import { GfsOfferTextBlock } from "@/services/backend/gfs/offer/types";
import { GfsOfferTextBlocksEditor } from "@/services/backend/gfs/offer/components/offer-config-editor";
import { useGfsOfferEditorContext } from "@/services/backend/gfs/offer/components/offer-editor-context";

export function GfsOfferCoverLetterPage() {
  const { offer, putRequest, updateContent, updateOfferDocument, saveState } =
    useGfsOfferEditorContext();
  const {
    data: config,
    error,
    isLoading,
  } = useGfsOfferConfigGetQuery({
    companyId: offer.companyId,
  });
  const { content } = putRequest;
  const document = offer.offerDocument;
  const documentCoverLetter = document.coverLetter ?? [];
  const documentClosingText = document.closingText ?? [];

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading || !config) return <Loader2 className="animate-spin" />;

  const coverLetterBlocks = documentCoverLetter.length
    ? documentCoverLetter
    : legacyTextBlock(
        "cover-letter",
        "Individueller Anschreibentext",
        content.coverLetter,
      );
  const closingTextBlocks = documentClosingText.length
    ? documentClosingText
    : legacyTextBlock(
        "closing-text",
        "Individueller Schlusstext",
        content.closingText,
      );

  return (
    <div className="space-y-4 pb-16">
      {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}
      <Card>
        <CardHeader>
          <CardTitle>{t("Anschreiben")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("Anrede")}</label>
            <InputDebounced
              value={content.salutation}
              disabled={offer.immutable}
              onChange={(event) =>
                updateContent({ salutation: event.target.value })
              }
            />
          </div>

          <GfsOfferTextBlocksEditor
            title="Anschreibenstexte"
            blocks={coverLetterBlocks}
            libraryBlocks={config.coverLetter}
            disabled={offer.immutable}
            onChange={(coverLetter) =>
              updateOfferDocument({
                ...document,
                coverLetter: preserveEmptySelection(
                  coverLetter,
                  content.coverLetter,
                  "custom-cover-letter",
                  "Individueller Anschreibentext",
                ),
              })
            }
          />

          <GfsOfferTextBlocksEditor
            title="Schlusstext"
            blocks={closingTextBlocks}
            libraryBlocks={config.closingText}
            disabled={offer.immutable}
            onChange={(closingText) =>
              updateOfferDocument({
                ...document,
                closingText: preserveEmptySelection(
                  closingText,
                  content.closingText,
                  "custom-closing-text",
                  "Individueller Schlusstext",
                ),
              })
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

function legacyTextBlock(
  id: string,
  title: string,
  body: string,
): GfsOfferTextBlock[] {
  if (!body.trim()) return [];
  return [
    {
      id: `custom-${id}`,
      title,
      body,
      selected: true,
      position: "after",
    },
  ];
}

function preserveEmptySelection(
  blocks: GfsOfferTextBlock[],
  fallbackBody: string,
  id: string,
  title: string,
) {
  if (blocks.length > 0 || !fallbackBody.trim()) return blocks;
  return [
    {
      id,
      title,
      body: fallbackBody,
      selected: false,
      position: "after",
    },
  ];
}
